import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { inventoryAPI, inventoryLocationsAPI } from '@shared/apiClient';

const MODE_LABELS = {
  receive: 'Receive Stock',
  adjust: 'Adjust Stock',
  transfer: 'Transfer Stock'
};

export default function InventoryStockActionModal({
  item,
  mode,
  isOpen,
  onClose,
  onSuccess,
  defaultLocationId = ''
}) {
  const [formData, setFormData] = useState({
    locationId: defaultLocationId,
    fromLocationId: defaultLocationId,
    toLocationId: '',
    quantity: '',
    newQuantity: '',
    reason: ''
  });

  const { data: locationsData } = useQuery(
    ['inventoryLocations'],
    () => inventoryLocationsAPI.getLocations(),
    { enabled: isOpen }
  );

  const { data: stockData } = useQuery(
    ['inventoryStock', item?.id],
    () => inventoryAPI.getStock(item.id),
    { enabled: isOpen && !!item?.id && mode === 'adjust' }
  );

  const locations = locationsData?.data?.locations || [];
  const stockRows = stockData?.data?.stock || [];

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      locationId: defaultLocationId || '',
      fromLocationId: defaultLocationId || '',
      toLocationId: '',
      quantity: '',
      newQuantity: '',
      reason: ''
    });
  }, [isOpen, defaultLocationId, mode]);

  const mutation = useMutation(
    async (payload) => {
      if (mode === 'receive') {
        return inventoryAPI.receiveStock(item.id, payload);
      }
      if (mode === 'adjust') {
        return inventoryAPI.adjustStock(item.id, payload);
      }
      return inventoryAPI.transferStock({ inventoryId: item.id, ...payload });
    },
    {
      onSuccess: () => {
        toast.success(`${MODE_LABELS[mode]} completed`);
        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Stock action failed');
      }
    }
  );

  if (!isOpen || !item || !mode) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (mode === 'receive') {
      mutation.mutate({
        locationId: formData.locationId,
        quantity: parseFloat(formData.quantity),
        reason: formData.reason || null
      });
      return;
    }

    if (mode === 'adjust') {
      mutation.mutate({
        locationId: formData.locationId,
        newQuantity: parseFloat(formData.newQuantity),
        reason: formData.reason || null
      });
      return;
    }

    mutation.mutate({
      fromLocationId: formData.fromLocationId,
      toLocationId: formData.toLocationId,
      quantity: parseFloat(formData.quantity),
      reason: formData.reason || null
    });
  };

  const selectedStockQty = stockRows.find((row) => row.locationId === formData.locationId);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{MODE_LABELS[mode]}</h3>
              <p className="text-sm text-gray-500">{item.itemNumber}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {(mode === 'receive' || mode === 'adjust') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={formData.locationId}
                  onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select location</option>
                  {locations.filter((loc) => loc.isActive).map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                {mode === 'adjust' && selectedStockQty && (
                  <p className="mt-1 text-xs text-gray-500">
                    Current qty: {parseFloat(selectedStockQty.quantityOnHand).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {mode === 'transfer' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Location</label>
                  <select
                    value={formData.fromLocationId}
                    onChange={(e) => setFormData({ ...formData, fromLocationId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select location</option>
                    {locations.filter((loc) => loc.isActive).map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Location</label>
                  <select
                    value={formData.toLocationId}
                    onChange={(e) => setFormData({ ...formData, toLocationId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select location</option>
                    {locations.filter((loc) => loc.isActive && loc.id !== formData.fromLocationId).map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {mode === 'adjust' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Quantity</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.newQuantity}
                  onChange={(e) => setFormData({ ...formData, newQuantity: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={mutation.isLoading}>
                {mutation.isLoading ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
